import { Formik, Form, Field } from "formik";
import { useEffect, useMemo, useState } from "react";
import { adminSchema } from "../../validation/userSchemas";

import ProInput from "../form/Input";
import { registerUser } from "../../api/authApi";
import { fetchRoles } from "../../api/accessControl";
import {
  Shield,
  Search,
  Loader,
  CheckCircle2,
  AlertCircle,
  UserRound,
  Mail,
  Phone,
  X,
} from "lucide-react";

export default function AddAdminForm({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [roles, setRoles] = useState([]);
  const [roleSearch, setRoleSearch] = useState("");
  const [rolesLoading, setRolesLoading] = useState(true);

  useEffect(() => {
    setRolesLoading(true);
    fetchRoles()
      .then(setRoles)
      .catch(() => setRoles([]))
      .finally(() => setRolesLoading(false));
  }, []);

  const handleSubmit = async (values) => {
    setServerError("");
    setSuccessMessage("");
    setLoading(true);

    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phoneNumber: values.phone,
      userType: "ADMIN",
      roles: values.roles,
    };

    const result = await registerUser(payload);

    if (result.success) {
      setLoading(false);
      setSuccessMessage("Admin registered successfully!");

      setTimeout(() => {
        onSuccess?.("Admin registered successfully!");
        onClose?.();
      }, 1500);
    } else {
      setLoading(false);
      setServerError(result.error?.message || "Failed to register admin.");
    }
  };

  const EnterpriseAlert = ({ type = "success", children }) => {
    const styles = {
      success:
        "border-green-200 bg-green-50 text-green-800 dark:border-green-900/40 dark:bg-green-950/30 dark:text-green-400",
      error:
        "border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-400",
    };

    const Icon = type === "success" ? CheckCircle2 : AlertCircle;

    return (
      <div
        className={`col-span-2 flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${styles[type]}`}
      >
        <Icon className="mt-0.5 h-4.5 w-4.5 shrink-0" />
        <div>{children}</div>
      </div>
    );
  };

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center rounded-3xl bg-slate-950/50 backdrop-blur-sm">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/10 shadow-lg">
            <Loader className="h-6 w-6 animate-spin text-white" />
          </div>
          <p className="mt-4 text-sm font-semibold tracking-wide text-white">
            Saving admin...
          </p>
          <p className="mt-1 text-xs text-white/70">
            Please wait while we complete the request.
          </p>
        </div>
      )}

      <Formik
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          roles: [],
        }}
        validationSchema={adminSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => {
  const filteredRoles = roles.filter((r) =>
    (r.name || "").toLowerCase().includes(roleSearch.toLowerCase())
  );

          return (
            <Form className="grid grid-cols-2 gap-6 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-950">
              <div className="col-span-2 flex items-start justify-between gap-4 border-b border-gray-200 pb-5 dark:border-gray-800">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-gray-200 bg-gray-50 text-gray-700 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
                    <Shield className="h-5 w-5" />
                  </div>

                  <div>
                    <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-white">
                      Add Administrator
                    </h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Create an administrator account and assign one or more roles.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => !loading && onClose?.()}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
                >
                  <X className="h-4.5 w-4.5" />
                </button>
              </div>

              {successMessage && (
                <EnterpriseAlert type="success">{successMessage}</EnterpriseAlert>
              )}

              {serverError && (
                <EnterpriseAlert type="error">{serverError}</EnterpriseAlert>
              )}

              <div className="col-span-2 rounded-2xl border border-gray-200 bg-gray-50/70 p-5 dark:border-gray-800 dark:bg-gray-900/50">
                <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                  <UserRound className="h-4 w-4" />
                  Basic Information
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Field
                      name="firstName"
                      label="First Name"
                      icon="👤"
                      component={ProInput}
                    />
                  </div>

                  <div>
                    <Field
                      name="lastName"
                      label="Last Name"
                      icon="👤"
                      component={ProInput}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Field
                      name="email"
                      label="Email Address"
                      icon="📧"
                      component={ProInput}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Field
                      name="phone"
                      label="Phone Number"
                      icon="📱"
                      component={ProInput}
                    />
                  </div>
                </div>
              </div>

              <div className="col-span-2 rounded-2xl border border-gray-200 bg-gray-50/70 p-5 dark:border-gray-800 dark:bg-gray-900/50">
                <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                  <Shield className="h-4 w-4" />
                  Assign Roles
                </div>

                <div className="relative mb-4">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search roles..."
                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:placeholder:text-gray-500"
                    value={roleSearch}
                    onChange={(e) => setRoleSearch(e.target.value)}
                  />
                </div>

                {values.roles.length > 0 && (
                  <div className="mb-4 flex flex-wrap gap-2">
                    {values.roles.map((role) => (
                      <span
                        key={role}
                        className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary dark:border-primary/20 dark:bg-primary/15"
                      >
                        {role}
                        <button
                          type="button"
                          onClick={() =>
                            setFieldValue(
                              "roles",
                              values.roles.filter((r) => r !== role)
                            )
                          }
                          className="rounded-full p-0.5 hover:bg-black/5 dark:hover:bg-white/10"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="max-h-72 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-950">
                  {rolesLoading ? (
                    <div className="flex items-center justify-center gap-3 px-4 py-10 text-sm text-gray-500 dark:text-gray-400">
                      <Loader className="h-4 w-4 animate-spin" />
                      Loading roles...
                    </div>
                  ) : filteredRoles.length > 0 ? (
                    <div className="space-y-2">
                      {filteredRoles.map((role) => {
                        const checked = values.roles.includes(role.name);

                        return (
                          <label
                            key={role.id}
                            className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition ${
                              checked
                                ? "border-primary/30 bg-primary/5 dark:border-primary/20 dark:bg-primary/10"
                                : "border-transparent hover:border-gray-200 hover:bg-gray-50 dark:hover:border-gray-700 dark:hover:bg-gray-900"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-600 dark:bg-gray-900"
                              checked={checked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFieldValue("roles", [...values.roles, role.name]);
                                } else {
                                  setFieldValue(
                                    "roles",
                                    values.roles.filter((r) => r !== role.name)
                                  );
                                }
                              }}
                            />

                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {role.name}
                              </div>
                              <div className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                                {role.description || "No description available."}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                      No roles available.
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-2 flex flex-col-reverse gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:justify-end dark:border-gray-800">
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => !loading && onClose?.()}
                  className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-60 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                >
                  {loading && <Loader className="h-4 w-4 animate-spin" />}
                  {loading ? "Saving Admin..." : "Save Admin"}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}